import { lazy, Suspense } from 'react';
import { Refine } from '@refinedev/core';
import {
  useNotificationProvider,
  ErrorComponent,
} from '@refinedev/antd';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import routerBindings, {
  UnsavedChangesNotifier,
  DocumentTitleHandler,
} from '@refinedev/react-router-v6';
import { ConfigProvider, App as AntApp, Spin } from 'antd';
import uzUZ from 'antd/locale/uz_UZ';

import { authProvider } from './providers/auth-provider';
import { dataProvider } from './providers/data-provider';
import { theme } from './lib/theme';
import { resources } from './config/resources';

// Pages - eagerly loaded (small / always needed)
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { UserList, UserShow } from './pages/users';
import { PassengerList, PassengerShow } from './pages/passengers';
import { DriverList, DriverShow, DriverCreate, SelfEmploymentCheckPage } from './pages/drivers';
import { InsuranceList } from './pages/insurance';
import { TariffList } from './pages/tariffs';
import { DocumentList, DocumentUserDocuments } from './pages/documents';
import { TripList, TripCreate, TripEdit, CancelledTripList } from './pages/trips';
import {
  DriverPostedTripList,
  DriverPostedTripShow,
  DriverTripBookingList,
  DriverTripBookingShow,
} from './pages/driver-trips';
import { StaffList, StaffCreate, StaffShow, StaffEdit } from './pages/staff';
import { SupportTicketList, SupportTicketShow } from './pages/support';
import { EmergencyChatList, EmergencyChatShow } from './pages/emergency';
import { SuggestionList, SuggestionShow } from './pages/suggestions';
import { FaqList, FaqCreate, FaqEdit } from './pages/faq';
import { CancellationReasonList, CancellationReasonCreate, CancellationReasonEdit } from './pages/cancellation-reasons';
import { FaollikBaliPage } from './pages/faollik-bali';
import { AppUpdateList, AppUpdateCreate, AppUpdateEdit } from './pages/app-updates';
import { PromoCodeList, PromoCodeCreate } from './pages/promo';
import {
  TrackingLinkList,
  TrackingLinkCreate,
  TrackingLinkShow,
} from './pages/tracking-links';
import { PricingPage } from './pages/pricing';
import { YolYolakayOfferList } from './pages/yol-yolakay';
import { ChatViewer } from './pages/chat';
import { AuditLogPage } from './pages/audit';
import { WithdrawalList } from './pages/withdrawals';
import { CarBrandsPage } from './pages/car-brands';
import { PaymentList } from './pages/payments';
import { IncentiveList } from './pages/incentives';
import { ReferralList } from './pages/referrals';
import {
  BusCompanyList,
  BusCompanyCreate,
  BusCompanyShow,
  BusTripList,
  BusTripCreate,
  BusTripEdit,
  BusBookingList,
  BusBookingShow,
  BusCompanyProfile,
  BusStaffList,
  BusStaffCreate,
} from './pages/bus';

// Pages - lazy loaded (large / heavy pages)
const TripShow = lazy(() => import('./pages/trips/TripShow').then(m => ({ default: m.TripShow })));
const BusTripShow = lazy(() => import('./pages/bus/BusTripShow').then(m => ({ default: m.BusTripShow })));
const BusBookingCreate = lazy(() => import('./pages/bus/BusBookingCreate').then(m => ({ default: m.BusBookingCreate })));
const LocationsPage = lazy(() => import('./pages/locations/LocationsPage').then(m => ({ default: m.LocationsPage })));
const TutorialsPage = lazy(() => import('./pages/tutorials/TutorialsPage').then(m => ({ default: m.TutorialsPage })));
const BusSettings = lazy(() => import('./pages/bus/BusSettings').then(m => ({ default: m.BusSettings })));
const BusReports = lazy(() => import('./pages/bus/BusReports').then(m => ({ default: m.BusReports })));
const AnalyticsDashboard = lazy(() => import('./pages/analytics/AnalyticsDashboard').then(m => ({ default: m.AnalyticsDashboard })));
const NotificationList = lazy(() => import('./pages/notifications/NotificationList').then(m => ({ default: m.NotificationList })));
const Settings = lazy(() => import('./pages/settings/Settings').then(m => ({ default: m.Settings })));
const LiveMap = lazy(() => import('./pages/livemap/LiveMap').then(m => ({ default: m.LiveMap })));
const TaxiReports = lazy(() => import('./pages/reports/TaxiReports').then(m => ({ default: m.TaxiReports })));

// Suspense fallback for lazy-loaded pages
const LazyFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
    <Spin size="large" />
  </div>
);

// Components
import { CustomLayout } from './components/layout/CustomLayout';

// Contexts
import { ServiceProvider } from './contexts/ServiceContext';

function App() {
  return (
    <BrowserRouter>
      <ConfigProvider theme={theme} locale={uzUZ}>
        <AntApp>
          <ServiceProvider>
            <Refine
              dataProvider={dataProvider}
              authProvider={authProvider}
              routerProvider={routerBindings}
              notificationProvider={useNotificationProvider}
              resources={resources}
              options={{
                syncWithLocation: true,
                warnWhenUnsavedChanges: true,
                useNewQueryKeys: true,
                projectId: 'safar-go-admin',
              }}
            >
              <Routes>
                <Route element={<CustomLayout />}>
                  <Route index element={<Dashboard />} />

                  {/* All Users */}
                  <Route path="/users">
                    <Route index element={<UserList />} />
                    <Route path=":id" element={<UserShow />} />
                  </Route>

                  {/* Passengers */}
                  <Route path="/passengers">
                    <Route index element={<PassengerList />} />
                    <Route path=":id" element={<PassengerShow />} />
                  </Route>

                  {/* Drivers */}
                  <Route path="/drivers">
                    <Route index element={<DriverList />} />
                    <Route path="create" element={<DriverCreate />} />
                    <Route path=":id" element={<DriverShow />} />
                  </Route>

                  <Route path="/self-employment-check" element={<SelfEmploymentCheckPage />} />

                  {/* Insurance */}
                  <Route path="/insurance" element={<InsuranceList />} />

                  {/* Tariffs */}
                  <Route path="/tariffs" element={<TariffList />} />

                  {/* Documents */}
                  <Route path="/documents">
                    <Route index element={<DocumentList />} />
                    <Route path=":userId" element={<DocumentUserDocuments />} />
                  </Route>

                  {/* Trips */}
                  <Route path="/trips">
                    <Route index element={<TripList />} />
                    <Route path="create" element={<TripCreate />} />
                    <Route path=":tripType/:id" element={<Suspense fallback={<LazyFallback />}><TripShow /></Suspense>} />
                    <Route path=":tripType/:id/edit" element={<TripEdit />} />
                  </Route>

                  {/* Cancelled Trips */}
                  <Route path="/cancelled-trips" element={<CancelledTripList />} />

                  {/* Driver Posted Trips */}
                  <Route path="/driver-posted-trips">
                    <Route index element={<DriverPostedTripList />} />
                    <Route path=":id" element={<DriverPostedTripShow />} />
                  </Route>

                  {/* Driver Trip Bookings */}
                  <Route path="/driver-trip-bookings">
                    <Route index element={<DriverTripBookingList />} />
                    <Route path=":id" element={<DriverTripBookingShow />} />
                  </Route>

                  {/* Bus Company Profile (for bus company users) */}
                  <Route path="/bus/company/me" element={<BusCompanyProfile />} />

                  {/* Bus Reports (for bus company users) */}
                  <Route path="/bus/reports" element={<Suspense fallback={<LazyFallback />}><BusReports /></Suspense>} />

                  {/* Bus Companies */}
                  <Route path="/bus/companies">
                    <Route index element={<BusCompanyList />} />
                    <Route path="create" element={<BusCompanyCreate />} />
                    <Route path=":id" element={<BusCompanyShow />} />
                  </Route>

                  {/* Bus Trips */}
                  <Route path="/bus/trips">
                    <Route index element={<BusTripList />} />
                    <Route path="create" element={<BusTripCreate />} />
                    <Route path=":id" element={<Suspense fallback={<LazyFallback />}><BusTripShow /></Suspense>} />
                    <Route path=":id/edit" element={<BusTripEdit />} />
                  </Route>

                  {/* Bus Bookings */}
                  <Route path="/bus/bookings">
                    <Route index element={<BusBookingList />} />
                    <Route path="create" element={<Suspense fallback={<LazyFallback />}><BusBookingCreate /></Suspense>} />
                    <Route path=":id" element={<BusBookingShow />} />
                  </Route>

                  {/* Bus Support */}
                  <Route path="/bus/support">
                    <Route index element={<SupportTicketList />} />
                    <Route path=":id" element={<SupportTicketShow />} />
                  </Route>

                  {/* Bus FAQ */}
                  <Route path="/bus/faq">
                    <Route index element={<FaqList />} />
                    <Route path="create" element={<FaqCreate />} />
                    <Route path=":id/edit" element={<FaqEdit />} />
                  </Route>

                  {/* Bus Promo Codes */}
                  <Route path="/bus/promo-codes">
                    <Route index element={<PromoCodeList />} />
                    <Route path="create" element={<PromoCodeCreate />} />
                  </Route>

                  {/* Bus Staff (Company Profiles) */}
                  <Route path="/bus/staff">
                    <Route index element={<BusStaffList />} />
                    <Route path="create" element={<BusStaffCreate />} />
                    <Route path=":id" element={<BusCompanyShow />} />
                  </Route>

                  {/* Bus Analytics */}
                  <Route path="/bus/analytics" element={<Suspense fallback={<LazyFallback />}><AnalyticsDashboard /></Suspense>} />

                  {/* Bus Payments */}
                  <Route path="/bus/payments" element={<PaymentList />} />

                  {/* Bus Notifications */}
                  <Route path="/bus/notifications" element={<Suspense fallback={<LazyFallback />}><NotificationList /></Suspense>} />

                  {/* Bus Settings */}
                  <Route path="/bus/settings" element={<Suspense fallback={<LazyFallback />}><BusSettings /></Suspense>} />

                  {/* Suggestions (Nimani yaxshilash kerak?) */}
                  <Route path="/suggestions">
                    <Route index element={<SuggestionList />} />
                    <Route path=":id" element={<SuggestionShow />} />
                  </Route>

                  {/* Emergency Chats */}
                  <Route path="/emergency">
                    <Route index element={<EmergencyChatList />} />
                    <Route path=":id" element={<EmergencyChatShow />} />
                  </Route>

                  {/* FAQ */}
                  <Route path="/faq">
                    <Route index element={<FaqList />} />
                    <Route path="create" element={<FaqCreate />} />
                    <Route path=":id/edit" element={<FaqEdit />} />
                  </Route>

                  {/* Cancellation Reasons */}
                  <Route path="/cancellation-reasons">
                    <Route index element={<CancellationReasonList />} />
                    <Route path="create" element={<CancellationReasonCreate />} />
                    <Route path=":id/edit" element={<CancellationReasonEdit />} />
                  </Route>

                  {/* Faollik bali (activity score rules & config) */}
                  <Route path="/faollik-bali" element={<FaollikBaliPage />} />

                  {/* App Updates */}
                  <Route path="/app-updates">
                    <Route index element={<AppUpdateList />} />
                    <Route path="create" element={<AppUpdateCreate />} />
                    <Route path=":id/edit" element={<AppUpdateEdit />} />
                  </Route>

                  {/* Promo Codes */}
                  <Route path="/promo-codes">
                    <Route index element={<PromoCodeList />} />
                    <Route path="create" element={<PromoCodeCreate />} />
                  </Route>

                  {/* Tracking Links (marketing) */}
                  <Route path="/tracking-links">
                    <Route index element={<TrackingLinkList />} />
                    <Route path="create" element={<TrackingLinkCreate />} />
                    <Route path=":id" element={<TrackingLinkShow />} />
                  </Route>

                  {/* Staff */}
                  <Route path="/staff">
                    <Route index element={<StaffList />} />
                    <Route path="create" element={<StaffCreate />} />
                    <Route path=":id" element={<StaffShow />} />
                    <Route path=":id/edit" element={<StaffEdit />} />
                  </Route>

                  {/* Car Brands */}
                  <Route path="/car-brands" element={<CarBrandsPage />} />

                  {/* Locations */}
                  <Route path="/locations" element={<Suspense fallback={<LazyFallback />}><LocationsPage /></Suspense>} />

                  {/* Tutorials */}
                  <Route path="/tutorials" element={<Suspense fallback={<LazyFallback />}><TutorialsPage /></Suspense>} />

                  {/* Pricing */}
                  <Route path="/pricing" element={<PricingPage />} />

                  {/* Taxi Reports */}
                  <Route path="/reports" element={<Suspense fallback={<LazyFallback />}><TaxiReports /></Suspense>} />

                  {/* Analytics */}
                  <Route path="/analytics" element={<Suspense fallback={<LazyFallback />}><AnalyticsDashboard /></Suspense>} />

                  {/* Payments */}
                  <Route path="/payments" element={<PaymentList />} />

                  {/* Live Map */}
                  <Route path="/live-map" element={<Suspense fallback={<LazyFallback />}><LiveMap /></Suspense>} />

                  {/* Notifications */}
                  <Route path="/notifications" element={<Suspense fallback={<LazyFallback />}><NotificationList /></Suspense>} />

                  {/* Incentives */}
                  <Route path="/incentives" element={<IncentiveList />} />

                  {/* Referrals */}
                  <Route path="/referrals" element={<ReferralList />} />

                  {/* Settings */}
                  <Route path="/settings" element={<Suspense fallback={<LazyFallback />}><Settings /></Suspense>} />

                  {/* Yol-Yolakay Client Offers */}
                  <Route path="/yol-yolakay-offers" element={<YolYolakayOfferList />} />

                  {/* Chat Viewer */}
                  <Route path="/chat" element={<ChatViewer />} />

                  {/* Audit Log */}
                  <Route path="/audit-logs" element={<AuditLogPage />} />

                  {/* Withdrawals */}
                  <Route path="/withdrawals" element={<WithdrawalList />} />

                  <Route path="*" element={<ErrorComponent />} />
                </Route>

                <Route path="/login" element={<Login />} />
              </Routes>

              <UnsavedChangesNotifier />
              <DocumentTitleHandler />
            </Refine>
          </ServiceProvider>
        </AntApp>
      </ConfigProvider>
    </BrowserRouter>
  );
}

export default App;
